import {
  Box,
  Collapse,
  CircularProgress,
  Fade,
  LinearProgress,
  Typography,
  useTheme,
} from "@mui/material";
import React from "react";
import LoadingButton from "../../../../components/LoadingButton";
import LucideIcon from "../../../../components/LucideIcon";
import {
  CompanyDetails,
  OnboardingStepKey,
  OnboardingStepStatus,
  PersonalizationState,
} from "../types";
import CompanyDetailsPanel, { CompanyDetailField } from "./CompanyDetailsPanel";

// Human-readable labels + icons for each backend personalization step key.
const STEP_META: Record<OnboardingStepKey, { label: string; icon: string; detail: string }> = {
  analyze: {
    label: "Analyzing your website",
    icon: "Globe",
    detail: "Reading your site to understand your business",
  },
  details: {
    label: "Building your company profile",
    icon: "Building2",
    detail: "Filling in your industry, size, and description",
  },
  profile: {
    label: "Building your Ideal Customer Profile",
    icon: "Target",
    detail: "Identifying who you should be selling to",
  },
  list: {
    label: "Finding your first leads",
    icon: "Users",
    detail: "Building a starter list of in-market buyers",
  },
  sequence: {
    label: "Writing your sales sequence",
    icon: "Mail",
    detail: "Drafting a 5-email outreach cadence for your ideal customer",
  },
  content: {
    label: "Creating your content plan",
    icon: "PenLine",
    detail: "Mapping content across the funnel",
  },
  samples: {
    label: "Designing sample content",
    icon: "LayoutGrid",
    detail: "Writing posts and generating on-brand images",
  },
  agents: {
    label: "Assembling your AI team",
    icon: "Users",
    detail: "Suggesting agents tailored to your goals",
  },
  widgets: {
    label: "Setting up your website agents",
    icon: "Globe",
    detail: "Configuring sales and support chat widgets for your site",
  },
};

interface PersonalizingStepProps {
  appName: string;
  firstName: string;
  state: PersonalizationState | null;
  // Editable company details (lifted to the parent so they can be saved on Continue).
  details: CompanyDetails;
  touched: Set<keyof CompanyDetails>;
  filledByAi: Set<CompanyDetailField>;
  onDetailChange: (field: keyof CompanyDetails, value: string) => void;
  onContinue: () => void;
  // Move forward to the result previews. Only used when the user resumed onto this screen
  // after the build already finished (Phase 2 complete) — the live flow auto-advances
  // instead, so no button is shown there.
  onProceed?: () => void;
  // True while the company edits are being persisted before advancing.
  saving?: boolean;
  // Forwarded to the company-details panel to authorize the logo upload.
  getToken: () => Promise<string>;
}

export default function PersonalizingStep({
  appName,
  firstName,
  state,
  details,
  touched,
  filledByAi,
  onDetailChange,
  onContinue,
  onProceed,
  saving = false,
  getToken,
}: PersonalizingStepProps) {
  const theme = useTheme();
  const steps = state?.steps || {};

  // Always show the FULL checklist (company profile -> ICP -> content -> AI team) so the
  // user sees everything we're building across both phases, with each step ticking off.
  const allSteps =
    state?.stepOrder ||
    ["details", "profile", "agents", "widgets", "list", "sequence", "content", "samples"];

  // We're still in the confirm gate until the user has clicked "Looks good" and Phase 2 has
  // kicked off (phase >= 2). The whole flow now lives on one screen: while confirming, the
  // company-details accordion is open under the "Building your company profile" step; once
  // confirmed, it collapses and the remaining steps build in place below it.
  const inPhase2 = (state?.phase || 1) >= 2;
  const isConfirm = !inPhase2;

  // Phase 1 errored (a step crashed) or the poll gave up waiting. We still let the user
  // continue — they have whatever they typed, and Phase 2 builds from that — so a failed
  // auto-fill never traps them on this screen.
  const phase1Failed = state?.status === "error";

  // The user may continue once Phase 1 has settled: it finished (awaiting confirmation /
  // complete) OR it failed (so they can still proceed manually).
  const phase1Done =
    state?.status === "awaiting_confirmation" ||
    state?.status === "complete" ||
    phase1Failed;

  const getSubtitle = () => {
    if (inPhase2)
      return "Confirmed! We're building your ideal customer profile, lead list, sales sequence, content, AI team, and website agents now. This takes a few minutes — feel free to keep watching, or close this tab and we'll email you the moment it's ready.";
    if (phase1Failed)
      return "We couldn't finish reading your business automatically. Fill in the details below and continue — we'll build everything from what you enter.";
    if (phase1Done)
      return "Here's what we found about your business. Review and fix anything, then continue and we'll build the rest.";
    return "We're reading your business so we can personalize everything. Review the details below and correct anything while we work.";
  };

  // Progress is measured across the WHOLE checklist (every step in both phases), always.
  // Measuring against just the phase-1 steps while confirming made the bar hit 100% after
  // two steps and then snap backward to ~25% on confirm; counting against all steps the
  // whole time keeps it climbing monotonically (e.g. 2/5 -> 3/5 -> ... -> done).
  const completedCount = allSteps.filter(
    (k) => steps[k]?.status === "complete" || steps[k]?.status === "error"
  ).length;
  const progress = allSteps.length
    ? Math.round((completedCount / allSteps.length) * 100)
    : 0;

  const renderStatusIcon = (status?: OnboardingStepStatus) => {
    if (status === "complete") {
      return <LucideIcon name="CheckCircle2" size={22} color={theme.palette.primary.main} />;
    }
    if (status === "error") {
      return <LucideIcon name="AlertCircle" size={22} color={theme.palette.error.main} />;
    }
    if (status === "generating") {
      return <CircularProgress size={18} />;
    }
    return <LucideIcon name="Circle" size={22} color={theme.palette.text.disabled} />;
  };

  return (
    <Fade in timeout={600}>
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1, color: "text.primary" }}>
          Welcome to {appName}{firstName ? `, ${firstName}` : ""} 🎉
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
          {getSubtitle()}
        </Typography>

        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ mb: 4, height: 8, borderRadius: 4 }}
        />

        {/* While Phase 2 builds, reassure the user they don't have to wait around — the work
            continues server-side and we email them when it's done. */}
        {inPhase2 && state?.status === "generating" && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 2,
              mb: 4,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "action.hover",
            }}
          >
            <LucideIcon name="Mail" size={20} color={theme.palette.primary.main} />
            <Typography variant="body2" color="text.secondary">
              This keeps building even if you leave — you can safely close this tab and we'll
              email you as soon as your workspace is ready.
            </Typography>
          </Box>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {allSteps.map((key) => {
            const meta = STEP_META[key as OnboardingStepKey] || {
              label: key,
              icon: "Sparkles",
              detail: "",
            };
            const status = steps[key]?.status;
            // The company-details review/edit panel lives under the "Building your company
            // profile" (details) step, since that's the step that fills those fields in.
            const isDetailsStep = key === "details";
            return (
              <Box
                key={key}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 2,
                  backgroundColor: "action.hover",
                  opacity: status === "pending" ? 0.6 : 1,
                  transition: "opacity 0.3s ease",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2 }}>
                  <Box sx={{ display: "flex", flexShrink: 0 }}>
                    {renderStatusIcon(status)}
                  </Box>
                  <Box sx={{ flexShrink: 0, color: "text.secondary" }}>
                    <LucideIcon name={meta.icon} size={20} />
                  </Box>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
                      {meta.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {status === "error"
                        ? "We couldn't finish this one — you can set it up later"
                        : status === "complete"
                        ? "Done"
                        : meta.detail}
                    </Typography>
                  </Box>
                </Box>

                {/* Phase 1 only: the editable company-details panel + Continue button are
                    nested under the details step. Once the user clicks "Looks good" and
                    Phase 2 starts, the panel is gone for good — it's not reopenable, since
                    Phase 2 is already building from the confirmed data — so we simply
                    collapse it away (no accordion header, no reopen control). */}
                {isDetailsStep && (
                  <Collapse in={isConfirm} unmountOnExit>
                    <Box sx={{ px: 2, pb: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                      <CompanyDetailsPanel
                        details={details}
                        touched={touched}
                        generating={!phase1Done}
                        filledByAi={filledByAi}
                        onChange={onDetailChange}
                        getToken={getToken}
                      />

                      <LoadingButton
                        variant="contained"
                        size="large"
                        onClick={onContinue}
                        disabled={!phase1Done}
                        loading={saving}
                        sx={{
                          alignSelf: "flex-start",
                          px: 4,
                          py: 1.5,
                          fontWeight: 600,
                          textTransform: "none",
                          borderRadius: 2,
                        }}
                      >
                        {phase1Failed
                          ? "Continue"
                          : phase1Done
                          ? "Looks good — continue"
                          : "Analyzing your business…"}
                      </LoadingButton>
                    </Box>
                  </Collapse>
                )}
              </Box>
            );
          })}
        </Box>

        {/* Forward button shown only when the user resumed onto an already-finished build
            (Phase 2 complete). The live flow auto-advances instead, so onProceed is omitted
            there and this never renders. Without it, a resumed/finished screen would have no
            way forward, since the "Looks good" button only exists during Phase 1 confirm. */}
        {onProceed && inPhase2 && state?.status === "complete" && (
          <LoadingButton
            variant="contained"
            size="large"
            onClick={onProceed}
            sx={{
              mt: 4,
              alignSelf: "flex-start",
              px: 4,
              py: 1.5,
              fontWeight: 600,
              textTransform: "none",
              borderRadius: 2,
            }}
          >
            See your results
          </LoadingButton>
        )}
      </Box>
    </Fade>
  );
}
